import { useCallback, useEffect, useMemo, useState } from "react";
import { ProjectOverviewLink } from "@/components/ProjectOverviewLink";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { TextInput } from "@/components/ui/Field";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { currentYear, yearDateRange } from "@/lib/auswertungen";
import {
  computeProjectCompletionLeadTimeDays,
  computeProjectCompletionStats,
  fetchProjectCompletions,
  filterProjectCompletions,
  formatLeadTimeDays,
  formatProjectCompletionAuftragLabel,
  formatProjectCompletionDateTime,
  getProjectCompletionClosedAt,
  getProjectCompletionPaidAt,
  getProjectCompletionStatusMeta,
  PROJECT_COMPLETION_STATUS_OPTIONS,
  projectCompletionStatusBadgeClass,
  sortProjectCompletions,
  type ProjectCompletionClientFilters,
  type ProjectCompletionRow,
  type ProjectCompletionStatusKey,
} from "@/lib/projectCompletions";
import { ProjectCompletionsFilterBar } from "@/pages/stundennachweise/ProjectCompletionsFilterBar";
import { Table, type Column } from "@/components/ui/Table";

const COMPLETION_STATUS_KEYS: ProjectCompletionStatusKey[] = [
  "open",
  "delivery_note_created",
  "invoiced",
  "paid",
];

const DEFAULT_FILTERS: ProjectCompletionClientFilters = {
  kunde: "",
  status: "all",
};

export function ProjectCompletionsReport() {
  const initialRange = useMemo(() => yearDateRange(currentYear()), []);
  const [from, setFrom] = useState(initialRange.from);
  const [to, setTo] = useState(initialRange.to);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [rows, setRows] = useState<ProjectCompletionRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchProjectCompletions({
        start: from,
        end: to,
        q: search.trim() || undefined,
        includeNachweisAbschluesse: true,
        limit: 2000,
      });
      setRows(data);
      setError(null);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Projektabschlüsse konnten nicht geladen werden.",
      );
    } finally {
      setLoading(false);
    }
  }, [from, to, search]);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    const list = filterProjectCompletions(rows ?? [], {
      ...filters,
      q: "",
    });
    return sortProjectCompletions(list);
  }, [rows, filters]);

  const stats = useMemo(() => computeProjectCompletionStats(filtered), [filtered]);

  const columns = useMemo<Column<ProjectCompletionRow>[]>(
    () => [
      {
        key: "auftrag",
        header: "Auftrag",
        sortable: true,
        sortValue: (row) => formatProjectCompletionAuftragLabel(row),
        render: (row) => (
          <ProjectOverviewLink
            auftragId={row.id}
            label={formatProjectCompletionAuftragLabel(row)}
          />
        ),
      },
      {
        key: "kunde",
        header: "Kunde",
        sortable: true,
        sortValue: (row) => row.kunde || "",
        render: (row) => row.kunde || "–",
      },
      {
        key: "ort",
        header: "Ort",
        sortable: true,
        sortValue: (row) => row.ort || "",
        render: (row) => row.ort || "–",
      },
      {
        key: "auftragsart",
        header: "Auftragsart",
        sortable: true,
        sortValue: (row) => row.auftragsart || "",
        render: (row) => row.auftragsart || "–",
      },
      {
        key: "status",
        header: "Status",
        sortable: true,
        sortValue: (row) => getProjectCompletionStatusMeta(row).label,
        render: (row) => {
          const status = getProjectCompletionStatusMeta(row);
          return (
            <span
              className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${projectCompletionStatusBadgeClass(status.key)}`}
            >
              {status.label}
            </span>
          );
        },
      },
      {
        key: "completed_at",
        header: "Abgeschlossen am",
        sortable: true,
        sortValue: (row) => getProjectCompletionClosedAt(row),
        render: (row) => formatProjectCompletionDateTime(getProjectCompletionClosedAt(row)),
      },
      {
        key: "paid_at",
        header: "Bezahlt am",
        sortable: true,
        sortValue: (row) => getProjectCompletionPaidAt(row),
        render: (row) => formatProjectCompletionDateTime(getProjectCompletionPaidAt(row)),
      },
      {
        key: "lead_time",
        header: "Durchlaufzeit",
        sortable: true,
        sortValue: (row) => computeProjectCompletionLeadTimeDays(row) ?? -1,
        render: (row) => formatLeadTimeDays(computeProjectCompletionLeadTimeDays(row)),
      },
    ],
    [],
  );

  const handleFromChange = (value: string) => {
    setFrom(value);
    if (value && to && value > to) setTo(value);
  };

  const handleToChange = (value: string) => {
    setTo(value);
    if (value && from && value < from) setFrom(value);
  };

  return (
    <div className="space-y-4">
      {error && (
        <Alert variant="error" title="Projektabschlüsse konnten nicht geladen werden">
          {error}
        </Alert>
      )}

      <section className="rounded-ios-lg bg-card px-4 py-4 shadow-ios-md ring-1 ring-separator/35">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <TextInput
            label="Suche"
            placeholder="Auftrag, Kunde …"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <TextInput
            label="Von"
            type="date"
            value={from}
            max={to || undefined}
            onChange={(e) => handleFromChange(e.target.value)}
          />
          <TextInput
            label="Bis"
            type="date"
            value={to}
            min={from || undefined}
            onChange={(e) => handleToChange(e.target.value)}
          />
          <div className="flex items-end">
            <Button variant="secondary" size="sm" onClick={() => void load()} loading={loading}>
              Neu laden
            </Button>
          </div>
        </div>
        <p className="mt-3 text-xs text-label-secondary">
          Alle abgeschlossenen Projekte (Festpreis und Nachweis). Zeitraumfilter nach Bezahldatum,
          sonst nach Abschlussdatum. Durchlaufzeit = Erstellung bis Kunde bezahlt.
        </p>
        <div className="mt-3">
          <ProjectCompletionsFilterBar
            filters={filters}
            searchQuery={search}
            rows={rows ?? []}
            onChange={(patch) => setFilters((prev) => ({ ...prev, ...patch }))}
          />
        </div>
      </section>

      {loading && rows === null ? (
        <div className="flex items-center justify-center py-16 text-label-secondary">
          <Spinner />
          <span className="ml-2 text-sm">Projektabschlüsse werden geladen …</span>
        </div>
      ) : (
        <>
          <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-ios-lg bg-card px-4 py-3 shadow-ios-md ring-1 ring-separator/35">
              <p className="text-xs text-label-secondary">Projekte im Zeitraum</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums text-label">{stats.total}</p>
            </div>
            <div className="rounded-ios-lg bg-card px-4 py-3 shadow-ios-md ring-1 ring-separator/35">
              <p className="text-xs text-label-secondary">Ø Durchlaufzeit (Erstellung → Bezahlt)</p>
              <p className="mt-1 text-2xl font-semibold text-label">
                {formatLeadTimeDays(stats.avgLeadTimeDays)}
              </p>
            </div>
            <div className="rounded-ios-lg bg-card px-4 py-3 shadow-ios-md ring-1 ring-separator/35 sm:col-span-2">
              <p className="text-xs text-label-secondary">Nach Status</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {COMPLETION_STATUS_KEYS.map((key) => (
                  <span
                    key={key}
                    className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${projectCompletionStatusBadgeClass(key)}`}
                  >
                    {PROJECT_COMPLETION_STATUS_OPTIONS.find((opt) => opt.value === key)?.label ??
                      key}
                    <span className="tabular-nums opacity-90">{stats.byStatus[key] ?? 0}</span>
                  </span>
                ))}
              </div>
            </div>
          </section>

          <section className="text-sm text-label-secondary">
            {filtered.length
              ? `${filtered.length} Einträge`
              : "Keine Projektabschlüsse im gewählten Zeitraum."}
          </section>

          <Table
            columns={columns}
            rows={filtered}
            rowKey={(row) => String(row.id ?? formatProjectCompletionAuftragLabel(row))}
            emptyText="Keine Projektabschlüsse im gewählten Zeitraum."
          />
        </>
      )}
    </div>
  );
}
